import React, { useState } from 'react';
import { Button, Popconfirm, message } from 'antd';
import { CloseOutlined, DeleteOutlined, PlusOutlined, LikeOutlined, LikeFilled, DislikeOutlined, DislikeFilled, SendOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import type { ChatSession } from '@nxsys/types/models/AiChat';
import { submitSessionFeedback } from '@nxsys/services/api/ai';
import {
  StyledSessionPanel,
  StyledSessionPanelHeader,
  StyledSessionList,
  StyledSessionItem,
  StyledNewChatButton,
  StyledHeaderButton,
  FeedbackCommentInput,
} from './index.styled';

type Props = {
  isOpen: boolean;
  sessions: ChatSession[];
  activeSessionId: string | undefined;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onNewChat: () => void;
  onClose: () => void;
};

const SessionList: React.FC<Props> = ({
  isOpen,
  sessions,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  onNewChat,
  onClose,
}) => {
  const theme = useTheme() as any;
  const [feedbackMap, setFeedbackMap] = useState<Record<string, 'up' | 'down'>>({});
  const [commentSessionId, setCommentSessionId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getGroupLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return 'Previous 7 days';
    if (diffDays <= 30) return 'Previous 30 days';
    return date.toLocaleDateString([], { month: 'long', year: 'numeric' });
  };

  const groupedSessions = sessions.reduce<{ label: string; items: ChatSession[] }[]>(
    (groups, session) => {
      const label = getGroupLabel(session.updatedAt);
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.items.push(session);
      } else {
        groups.push({ label, items: [session] });
      }
      return groups;
    },
    [],
  );

  const handleFeedback = async (e: React.MouseEvent, sessionId: string, rating: 'up' | 'down') => {
    e.stopPropagation();
    try {
      await submitSessionFeedback(sessionId, { rating });
      setFeedbackMap((prev) => ({ ...prev, [sessionId]: rating }));
      setCommentSessionId(sessionId);
      setComment('');
    } catch {
      message.error('Failed to submit feedback');
    }
  };

  const handleSubmitComment = async () => {
    if (!commentSessionId || !comment.trim()) return;
    const rating = feedbackMap[commentSessionId]
      || sessions.find((s) => s.id === commentSessionId)?.feedbackRating;
    if (!rating) return;
    setSubmitting(true);
    try {
      await submitSessionFeedback(commentSessionId, { rating, comment: comment.trim() });
      setCommentSessionId(null);
      setComment('');
      message.success('Feedback submitted');
    } catch {
      message.error('Failed to save comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getRating = (session: ChatSession): 'up' | 'down' | undefined =>
    feedbackMap[session.id] ?? session.feedbackRating;

  const iconBtnStyle: React.CSSProperties = {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '2px 4px', fontSize: 12, lineHeight: 1, flexShrink: 0,
  };

  return (
    <StyledSessionPanel $isOpen={isOpen} aria-hidden={!isOpen}>
      <StyledSessionPanelHeader>
        <span>Chat History</span>
        <StyledHeaderButton
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="Close session list"
        />
      </StyledSessionPanelHeader>
      <StyledNewChatButton
        type="primary"
        icon={<PlusOutlined />}
        onClick={onNewChat}
      >
        New Chat
      </StyledNewChatButton>
      <StyledSessionList>
        {groupedSessions.map((group) => (
          <div key={group.label}>
            <div style={{ padding: '8px 16px 4px', fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {group.label}
            </div>
            {group.items.map((session) => {
              const rating = getRating(session);
              const showComment = commentSessionId === session.id;
              return (
                <div key={session.id}>
                  <StyledSessionItem
                    $isActive={session.id === activeSessionId}
                    onClick={() => onSelectSession(session.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onSelectSession(session.id);
                    }}
                  >
                    <span className="session-title">{session.title || 'Untitled'}</span>
                    <button
                      onClick={(e) => handleFeedback(e, session.id, 'up')}
                      title="Thumbs up"
                      aria-label="Thumbs up"
                      style={{ ...iconBtnStyle, color: rating === 'up' ? theme.palette.success.main : theme.palette.text.disabled }}
                    >
                      {rating === 'up' ? <LikeFilled /> : <LikeOutlined />}
                    </button>
                    <button
                      onClick={(e) => handleFeedback(e, session.id, 'down')}
                      title="Thumbs down"
                      aria-label="Thumbs down"
                      style={{ ...iconBtnStyle, color: rating === 'down' ? theme.palette.error.main : theme.palette.text.disabled }}
                    >
                      {rating === 'down' ? <DislikeFilled /> : <DislikeOutlined />}
                    </button>
                    <Popconfirm
                      title="Delete this chat?"
                      onConfirm={(e) => {
                        e?.stopPropagation();
                        onDeleteSession(session.id);
                      }}
                      onCancel={(e) => e?.stopPropagation()}
                      okText="Delete"
                      okButtonProps={{ danger: true, size: 'small', type: 'link', style: { padding: '0 8px', height: 'auto', fontSize: 12 } }}
                      cancelButtonProps={{ size: 'small', type: 'link', style: { padding: '0 8px', height: 'auto', fontSize: 12 } }}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Delete session"
                        style={{ flexShrink: 0, marginLeft: 4, border: 'none', boxShadow: 'none' }}
                      />
                    </Popconfirm>
                  </StyledSessionItem>
                  {showComment && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 16px 8px' }}
                    >
                      <FeedbackCommentInput
                        placeholder="Add a comment..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleSubmitComment(); }
                          if (e.key === 'Escape') setCommentSessionId(null);
                        }}
                        autoFocus
                        style={{ height: 28, fontSize: 12 }}
                      />
                      <button
                        onClick={handleSubmitComment}
                        disabled={submitting || !comment.trim()}
                        style={{
                          ...iconBtnStyle,
                          color: submitting || !comment.trim() ? theme.palette.text.disabled : theme.palette.primary.main,
                          fontSize: 14,
                        }}
                        aria-label="Submit comment"
                      >
                        <SendOutlined />
                      </button>
                      <button
                        onClick={() => setCommentSessionId(null)}
                        style={{ ...iconBtnStyle, color: theme.palette.text.secondary, fontSize: 10 }}
                        aria-label="Cancel"
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </StyledSessionList>
    </StyledSessionPanel>
  );
};

export default SessionList;
